import { Injectable, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { PendingWorkService } from 'app/core/common/services/pending-work.service';
import { BehaviorSubject, fromEvent, merge, Subscription, timer } from 'rxjs';
import { filter, throttleTime } from 'rxjs/operators';

/**
 * Idle detection is CROSS-TAB: every tab writes its last-activity timestamp to
 * localStorage, and every timer decision re-reads that shared value before acting.
 * Without this, a forgotten background tab (the app opens reports via
 * window.open '_blank') would count as "idle", reach the lock screen invisibly,
 * and 5 minutes later revoke the server session + shared token — logging out the
 * tab the user is actively working in with no visible warning.
 */
const LAST_ACTIVITY_KEY = 'autoLogout:lastActivityAt';
const SIGNED_OUT_KEY = 'autoLogout:signedOutAt';
const SAVE_REQUESTED_KEY = 'autoLogout:saveRequestedAt';

@Injectable({
    providedIn: 'root',
})
export class AutoLogoutService {
    private inactivityTimer: Subscription | null = null;
    private lockScreenTimer: Subscription | null = null;
    private userActivitySubscription: Subscription | null = null;
    private inactivityTimeout = 600000; // 10 minutes in milliseconds
    private lockScreenTimeout = 300000; // 5 minutes in milliseconds
    private countdownSubject = new BehaviorSubject<number>(0); // Countdown observable
    private lastRoute: string = '/'; // Default to root if undefined
    /** Wall-clock instant this tab entered the lock screen; activity newer than this dismisses it. */
    private lockStartedAt = 0;
    /** In-memory fallback when localStorage is unavailable (private mode, quota). */
    private lastActivityFallback = Date.now();

    constructor(private router: Router,
        private ngZone: NgZone,
        private _authService: AuthService,
        private _pendingWorkService: PendingWorkService) {

        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                if (this.router.url !== '/confirmation-required') {
                    this.lastRoute = event.urlAfterRedirects;
                }
            });
    }

    // Access countdown as an observable
    getCountdown() {
        return this.countdownSubject.asObservable();
    }

    startMonitoring() {
        this.recordActivity(); // the tab just opened/booted — that's user activity
        this.armInactivityTimer();
        this.monitorUserActivity();
        window.addEventListener('storage', this.onStorageEvent);
    }

    private monitorUserActivity() {
        // Bubbling events can be observed straight off `document`. `scroll` does NOT
        // bubble, so it's observed in the capture phase to catch scrolling inside any
        // nested scroll container (report pages, dialogs, tables, …) — otherwise a
        // user reading/scrolling a long page registers as "idle" and gets logged out.
        const bubblingEvents = ['mousemove', 'mousedown', 'keydown', 'click', 'touchstart', 'wheel', 'input'];
        const streams = bubblingEvents.map(event => fromEvent(document, event));
        streams.push(fromEvent(document, 'scroll', { capture: true } as any));

        this.userActivitySubscription = merge(...streams)
            .pipe(throttleTime(1000)) // Throttle to avoid excessive resets
            .subscribe(() => {
                if (this.router.url === '/confirmation-required') {
                    // On the lock screen only the explicit "Continue" button counts as
                    // activity — stray mouse movement must not defuse the countdown,
                    // and it must not be broadcast as activity to other tabs either.
                    return;
                }
                this.recordActivity();
                this.armInactivityTimer();
            });
    }

    /** Publishes "the user is active right now" to every tab of this browser. */
    private recordActivity(): void {
        this.lastActivityFallback = Date.now();
        try {
            localStorage.setItem(LAST_ACTIVITY_KEY, String(this.lastActivityFallback));
        } catch {
            /* storage unavailable — fall back to per-tab behavior */
        }
    }

    /** Most recent activity across ALL tabs (falls back to this tab's own clock). */
    private getSharedLastActivity(): number {
        try {
            const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
            const parsed = raw ? parseInt(raw, 10) : NaN;
            if (!isNaN(parsed)) {
                // Guard against clock weirdness: never trust a future timestamp
                // beyond a small skew, and never return less than our own fallback.
                return Math.max(this.lastActivityFallback, Math.min(parsed, Date.now() + 5000));
            }
        } catch {
            /* ignore */
        }
        return this.lastActivityFallback;
    }

    /**
     * (Re)starts the inactivity timer. `delay` lets the fired-timer path re-arm with
     * just the remaining idle budget when another tab has been active in the meantime.
     */
    private armInactivityTimer(delay: number = this.inactivityTimeout) {
        if (this.inactivityTimer) {
            this.inactivityTimer.unsubscribe();
        }

        this.inactivityTimer = timer(Math.max(1000, delay)).subscribe(() => {
            this.onInactivityTimerFired();
        });
    }

    private onInactivityTimerFired(): void {
        // Signed out (or never signed in) — there is no session to protect, and
        // locking the sign-in page would just bounce the user around auth screens.
        if (!this._authService.accessToken) {
            return;
        }

        // The user may have been busy in ANOTHER tab the whole time. Only lock when
        // the whole browser has been idle for the full window; otherwise re-arm for
        // exactly the remaining budget.
        const idleFor = Date.now() - this.getSharedLastActivity();
        if (idleFor < this.inactivityTimeout) {
            this.armInactivityTimer(this.inactivityTimeout - idleFor);
            return;
        }

        this.navigateToLockScreen();
    }

    private navigateToLockScreen() {
        // Save unsaved work NOW, while the token is guaranteed valid and there is a
        // 5-minute buffer before any logout — not in the last instant before sign-out.
        // The broadcast makes every OTHER tab (each its own app instance with its own
        // registered savers) run the same sweep for its editors.
        this.requestSaveEverywhere();

        this.router.navigate(['/confirmation-required']);
        this.startLockScreenTimer();
    }

    /** Saves this tab's pending work and asks all other tabs to save theirs. */
    private requestSaveEverywhere(): void {
        try {
            localStorage.setItem(SAVE_REQUESTED_KEY, String(Date.now()));
        } catch {
            /* ignore */
        }
        this._pendingWorkService.saveAll().subscribe();
    }

    private startLockScreenTimer() {
        if (this.lockScreenTimer) {
            this.lockScreenTimer.unsubscribe();
        }

        this.lockStartedAt = Date.now();
        const totalSeconds = this.lockScreenTimeout / 1000;

        // The countdown is derived from wall-clock time, not from counting ticks:
        // browsers throttle background-tab timers to ~1 tick/minute, and a hidden
        // tab decrementing once per minute would stretch "5 minutes" into hours.
        this.lockScreenTimer = timer(0, 1000).subscribe(() => {
            // Activity in another tab since we locked → the user is not idle.
            // Dismiss the lock screen and return to work.
            if (this.getSharedLastActivity() > this.lockStartedAt) {
                this.dismissLockScreen();
                return;
            }

            // Session already ended elsewhere (another tab logged out / was kicked).
            if (!this._authService.accessToken) {
                this.lockScreenTimer?.unsubscribe();
                this.router.navigate(['/sign-in']);
                return;
            }

            const remaining = totalSeconds - Math.floor((Date.now() - this.lockStartedAt) / 1000);
            if (remaining <= 0) {
                this.lockScreenTimer?.unsubscribe();
                this.logoutUser();
            } else {
                this.countdownSubject.next(remaining);
            }
        });
    }

    /** Leaves the lock screen because activity was detected in another tab. */
    private dismissLockScreen(): void {
        this.lockScreenTimer?.unsubscribe();

        const idleFor = Date.now() - this.getSharedLastActivity();
        this.armInactivityTimer(Math.max(1000, this.inactivityTimeout - idleFor));

        if (this.router.url === '/confirmation-required') {
            this.router.navigate([this.getLastRoute()]);
        }
    }

    public continueSession() {
        if (this.lockScreenTimer) {
            this.lockScreenTimer.unsubscribe();
        }
        // "Continue" is explicit user activity — share it so any OTHER tab sitting
        // on its own lock screen dismisses too instead of logging everyone out.
        this.recordActivity();
        // Arm directly: at this point the URL is still '/confirmation-required', so
        // the activity subscription would no-op via its guard and leave the session
        // unprotected until the next stray event. The component navigates away right after.
        this.armInactivityTimer();
    }

    private logoutUser() {
        // Final safety net: while this (typically background, timer-throttled) tab
        // was counting down, the user may have resumed work elsewhere.
        if (this.getSharedLastActivity() > this.lockStartedAt) {
            this.dismissLockScreen();
            return;
        }

        // Last-chance save sweep for this tab (anything edited since the lock-screen
        // sweep, or whose earlier save failed). signOut() revokes the session and the
        // token, so it MUST NOT run until the saves have completed — saveAll() never
        // errors and is internally time-capped, so logout always goes through.
        this._pendingWorkService.saveAll().subscribe(() => {
            this.finishLogout();
        });
    }

    private finishLogout(): void {
        // Tell every other tab this was a deliberate idle-logout so they navigate to
        // sign-in immediately instead of dying on a 401 mid-request. (Their pending
        // work was saved by the lock-screen broadcast 5 minutes ago.)
        try {
            localStorage.setItem(SIGNED_OUT_KEY, String(Date.now()));
        } catch {
            /* ignore */
        }

        this._authService.signOut().subscribe();
        this.router.navigate(['/sign-in']); // Navigate to login on logout
    }

    /**
     * Fired when ANOTHER tab writes to localStorage (never fires in the writing tab).
     * Reacts to sign-out — either our explicit broadcast or the access token being
     * cleared (interceptor force-sign-in, kicked session) — by parking this tab on
     * the sign-in page instead of letting it fail on its next API call.
     */
    private onStorageEvent = (event: StorageEvent): void => {
        // Another tab reached its lock screen and asked everyone to persist unsaved
        // work while the session is still fully valid.
        if (event.key === SAVE_REQUESTED_KEY && event.newValue) {
            this._pendingWorkService.saveAll().subscribe();
            return;
        }

        const signedOutBroadcast = event.key === SIGNED_OUT_KEY && !!event.newValue;
        const tokenCleared = event.key === 'accessToken' && !event.newValue;
        if (!signedOutBroadcast && !tokenCleared) {
            return;
        }

        this.inactivityTimer?.unsubscribe();
        this.lockScreenTimer?.unsubscribe();

        if (!this.router.url.startsWith('/sign-in')) {
            this.router.navigate(['/sign-in']);
        }
    };

    stopMonitoring() {
        if (this.inactivityTimer) {
            this.inactivityTimer.unsubscribe();
        }
        if (this.userActivitySubscription) {
            this.userActivitySubscription.unsubscribe();
        }
        if (this.lockScreenTimer) {
            this.lockScreenTimer.unsubscribe();
        }
        window.removeEventListener('storage', this.onStorageEvent);
    }

    public getLastRoute(): string {
        return this.lastRoute;
    }
}
